import { FC, useState } from "react"
import { FaEdit, FaSave, FaTimes, FaTrash } from "react-icons/fa"
import { toast } from "react-toastify"

import { useTimezone } from "hooks/useTimezone"

import { TournamentService } from "services/tournaments/tournament.service"

import { PointRule, RulePreset } from "types/tournament.types"

import { PointRulesBuilder } from "./point-rules/PointRulesBuilder"
import { PresetRulesReadonly } from "./PresetRulesReadonly"

interface PresetCardProps {
  preset: RulePreset
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
  onUpdated: () => void
}

export const PresetCard: FC<PresetCardProps> = ({ preset, expanded, onToggle, onDelete, onUpdated }) => {
  const { formatDate } = useTimezone()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [rules, setRules] = useState<PointRule[]>([])

  const startEdit = () => {
    setName(preset.name)
    setDescription(preset.description || "")
    setRules(preset.rules || [])
    setEditing(true)
    if (!expanded) onToggle()
  }

  const cancelEdit = () => {
    setEditing(false)
    setName("")
    setDescription("")
    setRules([])
  }

  const handleUpdate = async () => {
    if (!name.trim()) {
      toast.error("Preset name is required")
      return
    }

    try {
      await TournamentService.updatePreset(preset.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        rules
      })
      toast.success("Preset updated!")
      cancelEdit()
      onUpdated()
    } catch {
      // silent
    }
  }

  return (
    <div className="bg-white dark:bg-[#0C0C1A] rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] shadow-sm overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.02)] transition-all"
        onClick={() => {
          if (!editing) onToggle()
        }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white truncate">{preset.name}</h4>
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
              {preset.rules?.length || 0} rules
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
              Used {preset.usage_count || 0}x
            </span>
          </div>
          {preset.description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">{preset.description}</p>}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Created {formatDate(preset.created_at)} &middot; Updated {formatDate(preset.updated_at)}
          </p>
        </div>

        <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={startEdit}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-all"
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] px-6 py-4">
          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-4 py-2 border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg dark:bg-[rgba(0,0,0,0.2)] dark:text-white focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
                  />
                </div>
              </div>

              <PointRulesBuilder value={rules} onChange={setRules} />

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={cancelEdit}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all text-sm"
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  className="flex items-center gap-2 px-4 py-2 bg-[rgba(139,92,246,1)] text-white rounded-lg hover:bg-[rgba(139,92,246,0.8)] transition-all text-sm"
                >
                  <FaSave /> Update Preset
                </button>
              </div>
            </div>
          ) : (
            <PresetRulesReadonly rules={preset.rules || []} />
          )}
        </div>
      )}
    </div>
  )
}
